import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { MapPin, Mail, Phone, Instagram, X } from 'lucide-react';
import regionsData from '../data/italyRegions.json';

/**
 * Dati di contatto per regione.
 * NB: verranno popolati man mano dal team. Le regioni senza dati mostrano
 * un messaggio "Referente in via di definizione".
 *
 * Struttura ammessa (array di referenti per supportare più persone):
 * 'IT_ID': {
 *   referenti: [
 *     {
 *       nome: 'Nome Cognome',
 *       ruolo: 'TP Referente',
 *       email: 'xyz@tecnicheperfette.com',
 *       telefono: '+39 ...',
 *       instagram: 'https://instagram.com/...',
 *     },
 *   ],
 *   note: 'testo libero',
 * }
 */
// Contatto di default (segreteria TP) — usato come placeholder per le regioni
// non ancora assegnate. Verrà sostituito man mano dal referente locale.
const DEFAULT_CONTACT = {
  referenti: [
    {
      nome: 'TP STAFF',
      ruolo: 'Segreteria',
      telefono: '+39 011 2341 6793',
      email: 'info@tecnicheperfette.com',
    },
  ],
};

const CONTACTS = {
  'IT23': {
    referenti: [
      {
        nome: 'SAGO',
        email: 'sagomc@hotmail.it',
        instagram: 'https://www.instagram.com/sagotherenzo',
      },
    ],
  },
  'IT25': {
    referenti: [
      {
        nome: "Luigi 'Gigi' Campagna",
        telefono: '+39 338 482 0605',
        email: 'luigi.served@gmail.com',
      },
    ],
  },
  'IT32': {
    referenti: [
      {
        nome: 'Davide Stefani',
        email: 'd.stefani@arci.bz.it',
        telefono: '345 787 2286',
      },
      {
        nome: 'Low',
        telefono: '+39 366 461 5031',
        email: 'low.officialtv@gmail.com',
      },
    ],
  },
  'IT34': {
    referenti: [
      {
        nome: "MAX MBASSADO'",
        email: 'mmbassado@hotmai.com',
      },
      {
        nome: 'DJEKO',
        telefono: '+39 342 120 8664',
      },
    ],
  },
  'IT42': {
    referenti: [
      {
        nome: 'Marco Dj Kamo',
        telefono: '+39 333 614 6570',
      },
      {
        nome: 'SNO',
        email: 'sno.artdirection@gmail.com',
        telefono: '346 180 6778',
      },
    ],
  },
  'IT45': {
    referenti: [
      {
        nome: 'Michele Ghironi',
        ruolo: 'Emilia',
        telefono: '345 519 1879',
        email: 'michele.ghironi@gmail.com',
        instagram: 'https://www.instagram.com/djover.wav',
      },
      {
        nome: 'IROL',
        ruolo: 'Romagna',
        telefono: '+39 366 306 7714',
        email: '62lollo62@gmail.com',
      },
    ],
  },
  'IT57': {
    referenti: [
      {
        nome: 'Jack Kosta Clan',
        telefono: '339 142 7411',
        email: 'costaklanprod@gmail.com',
      },
    ],
  },
  'IT62': {
    referenti: [
      {
        nome: 'Mattoncino',
        ruolo: 'Coordinatore Ateneo',
        telefono: '+39 346 690 9399',
      },
      {
        nome: 'Secco Baby',
        telefono: '+39 333 687 2560',
      },
    ],
  },
  'IT72': {
    referenti: [
      {
        nome: 'Mario Barbato',
        telefono: '347 905 8713',
        email: 'mario.barbato1804@gmail.com',
      },
    ],
  },
};

const TPStaffPage = () => {
  const [selected, setSelected] = useState(null);
  const [hovered, setHovered] = useState(null);

  const regions = useMemo(() => regionsData, []);

  const selectedRegion = selected ? regions.find((r) => r.id === selected) : null;
  const selectedContact = selected ? (CONTACTS[selected] || DEFAULT_CONTACT) : null;

  return (
    <div className="py-10 sm:py-16 px-4" style={{ backgroundColor: 'var(--tp-black)', minHeight: '80vh' }}>
      <div className="max-w-7xl mx-auto">
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl font-bold text-center mb-2 text-white"
          style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '3px' }}
        >
          TP STAFF
        </h1>
        <div className="w-24 h-1 mx-auto mb-4" style={{ backgroundColor: 'var(--tp-red)' }} />
        <p className="text-center text-gray-400 text-sm sm:text-base max-w-2xl mx-auto mb-10">
          Clicca su una regione per visualizzare i contatti del referente Tecniche Perfette di zona.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8 lg:gap-10 items-start">
          {/* Mappa */}
          <div className="relative mx-auto w-full" style={{ maxWidth: 720 }}>
          <svg
            viewBox="0 0 1000 1000"
            className="w-full h-auto"
            xmlns="http://www.w3.org/2000/svg"
            role="img"
            aria-label="Mappa interattiva delle regioni italiane"
          >
            {regions.map((r) => {
              const active = selected === r.id;
              const isHover = hovered === r.id;
              const fill = active
                ? '#dc2626'
                : isHover
                ? '#b91c1c'
                : '#000000';
              return (
                <path
                  key={r.id}
                  d={r.d}
                  data-region={r.id}
                  data-testid={`region-${r.id}`}
                  onClick={() => setSelected(r.id)}
                  onMouseEnter={() => setHovered(r.id)}
                  onMouseLeave={() => setHovered(null)}
                  role="button"
                  tabIndex={0}
                  aria-label={`Regione ${r.name}`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelected(r.id);
                    }
                  }}
                  style={{
                    fill,
                    stroke: '#3a3a3a',
                    strokeWidth: 1.2,
                    cursor: 'pointer',
                    transition: 'fill 200ms ease',
                    outline: 'none',
                  }}
                />
              );
            })}
          </svg>

          {/* Label che appare in hover */}
          {hovered && (
            <div
              className="pointer-events-none absolute top-3 left-3 px-3 py-1.5 rounded-full bg-black/80 border border-red-600/40 text-white text-xs sm:text-sm font-bold backdrop-blur-sm"
              style={{ fontFamily: 'Oswald, sans-serif', letterSpacing: '0.5px' }}
              data-testid="region-hover-label"
            >
              {regions.find((r) => r.id === hovered)?.name}
            </div>
          )}
          </div>

          {/* Box info TP STAFF (riferimenti centrali) */}
          <aside
            data-testid="tpstaff-info-box"
            className="rounded-xl border border-red-600/40 shadow-2xl p-5 sm:p-6 space-y-5"
            style={{ backgroundColor: '#0a0a0b' }}
          >
            <div>
              <h2
                className="text-2xl sm:text-3xl font-bold text-white leading-tight"
                style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '1.5px' }}
              >
                TP STAFF
              </h2>
              <a
                href="https://www.tecnicheperfette.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-red-400 hover:text-red-300 transition-colors"
              >
                www.tecnicheperfette.com
              </a>
              <div className="mt-3 space-y-1.5">
                <a
                  href="mailto:info@tecnicheperfette.com"
                  className="flex items-center gap-2 text-gray-200 hover:text-red-400 transition-colors text-sm"
                >
                  <Mail size={14} className="text-red-500 shrink-0" />
                  <span className="break-all">info@tecnicheperfette.com</span>
                </a>
                <a
                  href="mailto:luigi.cerved@gmail.com"
                  className="flex items-center gap-2 text-gray-200 hover:text-red-400 transition-colors text-sm"
                >
                  <Mail size={14} className="text-red-500 shrink-0" />
                  <span className="break-all">luigi.cerved@gmail.com</span>
                </a>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-800">
              <p
                className="text-[10px] uppercase tracking-widest text-gray-500 mb-2"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                Comunicazioni Business
              </p>
              <a
                href="mailto:info@masnetwork.it"
                className="flex items-center gap-2 text-gray-200 hover:text-red-400 transition-colors text-sm"
              >
                <Mail size={14} className="text-red-500 shrink-0" />
                <span className="break-all">info@masnetwork.it</span>
              </a>
            </div>

            <div className="pt-4 border-t border-gray-800">
              <p
                className="text-[10px] uppercase tracking-widest text-gray-500 mb-1"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                Book Manager
              </p>
              <p className="text-white font-bold text-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>
                Luigi 'Gigi' Campagna
              </p>
              <a
                href="tel:+393384820605"
                className="flex items-center gap-2 text-gray-300 hover:text-red-400 transition-colors text-sm mt-1"
              >
                <Phone size={14} className="text-red-500 shrink-0" />
                <span>+39 338 482 0605</span>
              </a>
            </div>

            <div className="pt-4 border-t border-gray-800">
              <p
                className="text-[10px] uppercase tracking-widest text-gray-500 mb-1"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                Riferimento Tecnico e Tour Manager
              </p>
              <p className="text-white font-bold text-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>
                Lorenzo Caschetto
              </p>
              <a
                href="tel:+393403827588"
                className="flex items-center gap-2 text-gray-300 hover:text-red-400 transition-colors text-sm mt-1"
              >
                <Phone size={14} className="text-red-500 shrink-0" />
                <span>+39 340 382 7588</span>
              </a>
            </div>

            <div className="pt-4 border-t border-gray-800">
              <p
                className="text-[10px] uppercase tracking-widest text-gray-500 mb-1"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                Responsabile Web e Social
              </p>
              <p className="text-white font-bold text-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>
                Serse Rossini
              </p>
              <a
                href="tel:+393280172092"
                className="flex items-center gap-2 text-gray-300 hover:text-red-400 transition-colors text-sm mt-1"
              >
                <Phone size={14} className="text-red-500 shrink-0" />
                <span>+39 328 017 2092</span>
              </a>
            </div>
          </aside>
        </div>

        {/* Hint mobile */}
        <p className="text-center text-xs text-gray-500 mt-6 sm:hidden">
          Tocca una regione per aprire la scheda referente
        </p>
      </div>

      {/* Modal contatti regione */}
      {selectedRegion && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Contatti staff ${selectedRegion.name}`}
          data-testid="region-contact-modal"
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(8px)' }}
          onClick={() => setSelected(null)}
        >
          <div
            className="relative w-full max-w-md rounded-xl overflow-hidden border border-red-600/40 shadow-2xl"
            style={{ backgroundColor: '#0a0a0b' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800 bg-gradient-to-r from-red-900/40 to-transparent">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center shrink-0">
                  <MapPin size={18} className="text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-widest text-gray-400" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    TP Staff — Regione
                  </p>
                  <h3
                    className="text-xl sm:text-2xl font-bold text-white leading-tight truncate"
                    style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '1px' }}
                  >
                    {selectedRegion.name}
                  </h3>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelected(null)}
                data-testid="region-modal-close"
                aria-label="Chiudi"
                className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gray-800 hover:bg-gray-700 text-white transition-colors shrink-0"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-6">
              {selectedContact && selectedContact.referenti && selectedContact.referenti.length > 0 ? (
                <div className="space-y-5">
                  {selectedContact.referenti.map((ref, idx) => (
                    <div
                      key={idx}
                      className={idx > 0 ? 'pt-5 border-t border-gray-800' : ''}
                      data-testid={`region-referente-${idx}`}
                    >
                      {ref.nome && (
                        <div className="mb-3">
                          <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>
                            {selectedContact.referenti.length > 1 ? `Referente ${idx + 1}` : 'Referente'}
                          </p>
                          <p className="text-white text-lg font-bold" style={{ fontFamily: 'Oswald, sans-serif' }}>
                            {ref.nome}
                          </p>
                          {ref.ruolo && (
                            <p className="text-gray-400 text-sm">{ref.ruolo}</p>
                          )}
                        </div>
                      )}

                      <div className="space-y-2">
                        {ref.email && (
                          <a
                            href={`mailto:${ref.email}`}
                            className="flex items-center gap-3 text-gray-200 hover:text-red-400 transition-colors"
                            data-testid={`region-referente-${idx}-email`}
                          >
                            <Mail size={16} className="text-red-500 shrink-0" />
                            <span className="text-sm break-all">{ref.email}</span>
                          </a>
                        )}
                        {ref.telefono && (
                          <a
                            href={`tel:${ref.telefono.replace(/\s/g, '')}`}
                            className="flex items-center gap-3 text-gray-200 hover:text-red-400 transition-colors"
                            data-testid={`region-referente-${idx}-phone`}
                          >
                            <Phone size={16} className="text-red-500 shrink-0" />
                            <span className="text-sm">{ref.telefono}</span>
                          </a>
                        )}
                        {ref.instagram && (
                          <a
                            href={ref.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 text-gray-200 hover:text-red-400 transition-colors"
                            data-testid={`region-referente-${idx}-instagram`}
                          >
                            <Instagram size={16} className="text-red-500 shrink-0" />
                            <span className="text-sm">Instagram</span>
                          </a>
                        )}
                      </div>
                    </div>
                  ))}

                  {selectedContact.note && (
                    <p className="text-xs text-gray-400 leading-relaxed pt-3 border-t border-gray-800">
                      {selectedContact.note}
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-center py-6">
                  <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center bg-gray-800">
                    <MapPin size={22} className="text-gray-500" />
                  </div>
                  <p className="text-white font-bold text-base mb-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    Referente in via di definizione
                  </p>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    Stiamo formando lo staff di zona per <span className="text-white font-semibold">{selectedRegion.name}</span>.<br />
                    I contatti verranno pubblicati a breve.
                  </p>
                  <a
                    href="/contatti"
                    className="inline-flex items-center gap-2 mt-5 px-5 py-2.5 rounded-full bg-red-600 hover:bg-red-500 text-white font-bold text-xs transition-colors"
                    style={{ fontFamily: 'Oswald, sans-serif', letterSpacing: '0.5px' }}
                    data-testid="region-contact-fallback"
                  >
                    <Mail size={14} />
                    CONTATTACI
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default TPStaffPage;
